import { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { SquaresFour, BookBookmark, UploadSimple, Flag, ListChecks, Detective, UsersThree, GearSix, SignOut, List, X, ArrowSquareOut } from "@phosphor-icons/react";
import { Mascot } from "@/components/styleguide/Mascot";
import { cn } from "@/lib/utils";
import { useAuth } from "../AuthContext";

const NAV = [
  { to: "/admin", label: "Dashboard", icon: SquaresFour, end: true },
  { to: "/admin/words", label: "Master Vocabulary", icon: BookBookmark },
  { to: "/admin/import", label: "Import Words", icon: UploadSimple },
  { to: "/admin/review", label: "Review Queue", icon: ListChecks },
  { to: "/admin/missing", label: "Missing Words", icon: Detective },
  { to: "/admin/users", label: "Users", icon: UsersThree },
  { to: "/admin/flags",    label: "Flags & Messages", icon: Flag },
  { to: "/admin/settings", label: "Settings",       icon: GearSix },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const nav = (
    <nav className="flex flex-col gap-1">
      {NAV.map(({ to, label, icon: Icon, end }) => (
        <NavLink
          key={to}
          to={to}
          end={end}
          onClick={() => setOpen(false)}
          data-testid={`admin-nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
          className={({ isActive }) =>
            cn(
              "flex min-h-[44px] items-center gap-2.5 rounded-xl px-3 text-sm font-bold transition-colors",
              isActive ? "bg-primary/12 text-primary-strong" : "text-muted-foreground hover:bg-muted hover:text-foreground",
            )
          }
        >
          <Icon size={17} weight="bold" /> {label}
        </NavLink>
      ))}
    </nav>
  );

  return (
    <div className="flex min-h-[100svh] bg-background">
      {/* desktop sidebar */}
      <aside className="hidden w-60 shrink-0 flex-col border-r border-border bg-card px-4 py-5 lg:flex">
        <div className="mb-6 flex items-center gap-2 px-1">
          <Mascot size={28} animated={false} />
          <div className="leading-tight">
            <p className="font-display text-base font-black text-foreground">Bloom Admin</p>
            <p className="text-[10px] font-extrabold uppercase tracking-wider text-muted-foreground">Vocabulary Portal</p>
          </div>
        </div>
        {nav}
        <div className="mt-auto space-y-1 pt-6">
          <a href="/vocab/children" className="flex min-h-[40px] items-center gap-2.5 rounded-xl px-3 text-sm font-bold text-muted-foreground transition hover:bg-muted hover:text-foreground" data-testid="admin-exit-link">
            <ArrowSquareOut size={16} weight="bold" /> Exit to app
          </a>
          <button
            data-testid="admin-logout-btn"
            onClick={async () => { await logout(); navigate("/vocab"); }}
            className="flex min-h-[40px] w-full items-center gap-2.5 rounded-xl px-3 text-sm font-bold text-muted-foreground transition hover:bg-muted hover:text-foreground"
          >
            <SignOut size={16} weight="bold" /> Sign out
          </button>
          <p className="truncate px-3 pt-2 text-[10px] font-bold text-muted-foreground">{user?.email}</p>
        </div>
      </aside>

      {/* mobile top bar */}
      <div className="flex min-w-0 flex-1 flex-col">
        <div className="flex items-center justify-between border-b border-border bg-card px-4 py-2.5 lg:hidden">
          <div className="flex items-center gap-2">
            <Mascot size={24} animated={false} />
            <span className="font-display text-sm font-black text-foreground">Bloom Admin</span>
          </div>
          <button onClick={() => setOpen(!open)} aria-label="Menu" data-testid="admin-menu-btn" className="flex h-11 w-11 items-center justify-center rounded-full bg-muted">
            {open ? <X size={18} weight="bold" /> : <List size={18} weight="bold" />}
          </button>
        </div>
        {open && (
          <div className="border-b border-border bg-card p-4 lg:hidden">
            {nav}
            {/* Sign out + exit — were missing from mobile menu */}
            <div className="mt-3 border-t border-border pt-3 space-y-1">
              <a href="/vocab/children"
                onClick={() => setOpen(false)}
                className="flex min-h-[44px] items-center gap-2.5 rounded-xl px-3 text-sm font-bold text-muted-foreground transition hover:bg-muted hover:text-foreground"
                data-testid="admin-exit-link-mobile">
                <ArrowSquareOut size={16} weight="bold" /> Exit to app
              </a>
              <button
                data-testid="admin-logout-btn-mobile"
                onClick={async () => { await logout(); navigate("/vocab"); setOpen(false); }}
                className="flex min-h-[44px] w-full items-center gap-2.5 rounded-xl px-3 text-sm font-bold text-muted-foreground transition hover:bg-muted hover:text-foreground"
              >
                <SignOut size={16} weight="bold" /> Sign out
              </button>
              <p className="truncate px-3 pt-1 text-[10px] font-bold text-muted-foreground">{user?.email}</p>
            </div>
          </div>
        )}

        <main className="min-w-0 flex-1 px-4 py-6 sm:px-6 lg:px-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}